import { StrictMode, useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import reportWebVitals from './reportWebVitals.ts'
import type { createAppRouter } from '@/app/router'
import type { LegalAccessState } from '@/features/legal/legal-access'
import { McpAuthorizationPage } from '@/features/mcp-management/oauth/authorization/mcp-authorization-page'
import { bootstrapApp } from '@/app/bootstrap'
import { AppProviders } from '@/app/providers'
import { createAppRouter as buildAppRouter } from '@/app/router'
import { AppErrorBoundary } from '@/observability/app-error-boundary'
import {
  initializeMonitoring,
  monitoringRootOptions,
  reportFrontendError,
} from '@/observability/monitoring'
import {
  PwaInstallPromptController,
  PwaServiceWorkerController,
} from '@/pwa/pwa-controller'
import { StartupFallback } from '@/pwa/startup-fallback'
import { LoginPage } from '@/pages/login/login-page'
import { CUSTOMER_AUTH_SESSION_EXPIRED_EVENT } from '@/shared/lib/customer-auth'
import { LegalAcceptancePage } from '@/features/legal/legal-acceptance-page'
import * as I18nextProvider from '@/integrations/i18next/root-provider'

import './styles.css'

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}

type StartupState =
  | {
      status: 'booting'
    }
  | {
      router: ReturnType<typeof createAppRouter>
      status: 'ready'
      providers: AuthenticatedBootstrap['providers']
    }
  | {
      error: unknown
      status: 'error'
    }
  | {
      status: 'unauthenticated'
    }
  | {
      status: 'legal-pending'
      legalState: Extract<LegalAccessState, { mode: 'pending' }>
    }

type AuthenticatedBootstrap = Extract<
  Awaited<ReturnType<typeof bootstrapApp>>,
  { authenticated: true }
>

let appBootstrapPromise: ReturnType<typeof bootstrapApp> | null = null

const rootElement = document.getElementById('app')
if (rootElement) {
  document.documentElement.setAttribute('data-commit-hash', __APP_COMMIT_HASH__)
  document.documentElement.setAttribute(
    'data-build-number',
    __APP_BUILD_NUMBER__,
  )
  document.documentElement.setAttribute('data-build-time', __APP_BUILD_TIME__)
}

initializeMonitoring()

if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, monitoringRootOptions)

  root.render(
    <I18nextProvider.Provider>
      <StrictMode>
        <AppErrorBoundary>
          <PwaServiceWorkerController>
            {window.location.pathname === '/mcp/authorize' ? (
              <McpAuthorizationPage />
            ) : (
              <App />
            )}
          </PwaServiceWorkerController>
        </AppErrorBoundary>
      </StrictMode>
    </I18nextProvider.Provider>,
  )
}

reportWebVitals()

function App() {
  const [startupState, setStartupState] = useState<StartupState>({
    status: 'booting',
  })

  useEffect(() => {
    let isMounted = true
    const handleSessionExpired = () => {
      if (window.location.pathname !== '/login') {
        const returnTo = encodeURIComponent(
          window.location.pathname + window.location.search,
        )
        window.location.assign(
          `/login?reason=session_expired&returnTo=${returnTo}`,
        )
      }
    }

    window.addEventListener(
      CUSTOMER_AUTH_SESSION_EXPIRED_EVENT,
      handleSessionExpired,
    )

    void getAppBootstrapPromise()
      .then((bootstrapState) => {
        if (!isMounted) {
          return
        }

        if (!bootstrapState.authenticated) {
          setStartupState(
            'legalState' in bootstrapState
              ? {
                  status: 'legal-pending',
                  legalState: bootstrapState.legalState,
                }
              : { status: 'unauthenticated' },
          )

          return
        }

        const { records, currentUser, providers } = bootstrapState

        setStartupState({
          providers,
          router: buildAppRouter({ providers, currentUser, records }),
          status: 'ready',
        })
      })
      .catch((error: unknown) => {
        if (!isMounted) {
          return
        }

        reportFrontendError(error, 'bootstrap')

        setStartupState({
          error,
          status: 'error',
        })
      })

    return () => {
      isMounted = false
      window.removeEventListener(
        CUSTOMER_AUTH_SESSION_EXPIRED_EVENT,
        handleSessionExpired,
      )
    }
  }, [])

  if (startupState.status === 'booting') {
    return null
  }

  if (startupState.status === 'error') {
    return <StartupFallback error={startupState.error} />
  }

  if (startupState.status === 'unauthenticated') {
    return <LoginPage />
  }

  if (startupState.status === 'legal-pending') {
    return <LegalAcceptancePage state={startupState.legalState} />
  }

  return (
    <AppProviders context={startupState.providers}>
      <PwaInstallPromptController />
      <RouterProvider router={startupState.router} />
    </AppProviders>
  )
}

function getAppBootstrapPromise() {
  appBootstrapPromise ??= bootstrapApp()

  return appBootstrapPromise
}
